import { EffectType, MATERIAL_STATUS, previewableStatus, reviewingReviewStatus, REVIEW_STATUS, selfOnlyReviewFailedReviewStatus, selfViewSecurityReviewFailedReviewStatus, selfOnlySecurityReviewFailedReviewStatus } from '../typings';
// PURE 标记立即执行函数无副作用，打包到该文件时可以不被引入
// 增加类型注释，确保所有的 CollectionEffectType 都有定义，新增加 CollectionEffectType 时也不会遗漏这里
const isCollectionMap = /*#__PURE__*/(() => ({
  [EffectType.WordArtCollection]: true,
  [EffectType.StickerCollection]: true,
  [EffectType.AudioEffectCollection]: true,
  [EffectType.VideoCollection]: true,
  [EffectType.TextTemplateCollection]: true,
  [EffectType.FilterCollection]: true
}))();
export function checkIsCollection(effectType) {
  return isCollectionMap[effectType] === true;
}
export function checkIsVip(commonAttr) {
  return getMaterialBusinessInfo(commonAttr)?.is_vip || false;
}
export function getMaterialBusinessInfo(commonAttr) {
  const {
    business_info
  } = commonAttr;
  if (business_info?.json) {
    return business_info?.json || {};
  }
  try {
    return JSON.parse(business_info?.json_str || '{}') || {};
  } catch (e) {
    return {};
  }
}
export function getMaterialExtra(commonAttr) {
  const {
    extra,
    extra_json
  } = commonAttr;
  if (extra_json) {
    return extra_json;
  }
  try {
    return JSON.parse(extra || '{}') || {};
  } catch (e) {
    return {};
  }
}

// PURE 标记立即执行函数无副作用，打包到该文件时可以不被引入
// 增加类型注释，确保所有的 CollectionEffectType 都有定义，新增加 CollectionEffectType 时也不会遗漏这里
const singleTypeMap = /*#__PURE__*/(() => ({
  [EffectType.WordArtCollection]: EffectType.WordArt,
  [EffectType.StickerCollection]: EffectType.Sticker,
  [EffectType.AudioEffectCollection]: EffectType.AudioEffect,
  [EffectType.VideoCollection]: EffectType.Video,
  [EffectType.TextTemplateCollection]: EffectType.TextTemplate,
  [EffectType.FilterCollection]: EffectType.Filter
}))();

/**
 * - `asCollection` 为 false 时（默认），传入合集类型会返回对应的单品类型
 * - `asCollection` 为 true 时，传入什么类型就返回什么类型
 */
export function getEffectType(effectType, asCollection = false) {
  if (asCollection) {
    return effectType;
  }
  return singleTypeMap[effectType] ?? effectType;
}
export function checkIsPreviewable(commonAttr, options) {
  const {
    status
  } = commonAttr;
  // 删除和下线不能预览
  if (status === MATERIAL_STATUS.deleted || status === MATERIAL_STATUS.offline) {
    return false;
  }
  // 审核不通过，不能预览
  if (checkIsSecurityReviewFailed(commonAttr) || checkIsQualityReviewFailed(commonAttr) || checkIsCopyrightReviewFailed(commonAttr)) {
    return false;
  }
  // 主态的话，审核中、审核通过的素材都可以预览
  if (options?.isSelf) {
    return true;
  }
  // 客态只有上线、个见可预览
  return previewableStatus.includes(status);
}
export function checkIsReviewing(commonAttr) {
  const {
    status,
    review_info
  } = commonAttr;
  // 用户发布后待审
  if (status === MATERIAL_STATUS.preAudit) {
    return true;
  }
  // 审核中，会被设为自见，看 reviewStatus
  const reviewStatus = review_info?.review_status;
  if (status === MATERIAL_STATUS.selfView) {
    return reviewingReviewStatus.includes(reviewStatus);
  }
  return false;
}
export function checkIsSelfOnlyReviewFailed(commonAttr) {
  const {
    status,
    review_info
  } = commonAttr;
  const reviewStatus = review_info?.review_status;
  if (status === MATERIAL_STATUS.selfOnly) {
    return selfOnlyReviewFailedReviewStatus.includes(reviewStatus);
  }
  return false;
}
export function checkIsSecurityReviewFailed(commonAttr) {
  const {
    status,
    review_info
  } = commonAttr;
  const reviewStatus = review_info?.review_status;
  if (status === MATERIAL_STATUS.banned) {
    // 版权审核也会 status === banned，需要排除掉
    return reviewStatus !== REVIEW_STATUS.copyrightReviewFailed;
  }
  if (status === MATERIAL_STATUS.selfView) {
    return selfViewSecurityReviewFailedReviewStatus.includes(reviewStatus);
  }
  if (status === MATERIAL_STATUS.selfOnly) {
    return selfOnlySecurityReviewFailedReviewStatus.includes(reviewStatus);
  }
  return false;
}
export function checkIsQualityReviewFailed(commonAttr) {
  const {
    status,
    review_info
  } = commonAttr;
  const reviewStatus = review_info?.review_status;
  return status === MATERIAL_STATUS.selfOnly && reviewStatus === REVIEW_STATUS.qualityReviewFailed;
}
export function checkIsCopyrightReviewFailed(commonAttr) {
  const {
    status,
    review_info
  } = commonAttr;
  const reviewStatus = review_info?.review_status;
  if (reviewStatus === REVIEW_STATUS.copyrightReviewFailed) {
    return status === MATERIAL_STATUS.banned || status === MATERIAL_STATUS.selfView;
  }
  return false;
}
export function checkIsSimilarReviewFailed(commonAttr) {
  const {
    status,
    review_info
  } = commonAttr;
  const reviewStatus = review_info?.review_status;
  return status === MATERIAL_STATUS.selfOnly && reviewStatus === REVIEW_STATUS.similarReviewFailed;
}
export function getMaterialVideo(material) {
  switch (material.common_attr.effect_type) {
    case EffectType.Video:
      return material.video;
    case EffectType.Formula:
      return material.recipe.video;
    default:
      return null;
  }
}
export function getVideoInfo(material) {
  const materialVideo = getMaterialVideo(material);
  if (!materialVideo) {
    return null;
  }
  const {
    transcoded_video,
    origin_video
  } = materialVideo;
  let videoInfo = origin_video;
  if (transcoded_video) {
    videoInfo = transcoded_video['720p'] || transcoded_video['480p'] || transcoded_video['360p'] || videoInfo;
  }
  return videoInfo;
}

/** 单位：秒 */
export function getMusicDuration(material) {
  switch (material.common_attr.effect_type) {
    case EffectType.AudioEffect:
      return material.audio_effect.duration;
    case EffectType.Music:
      return material.song.duration;
    default:
      return null;
  }
}